// Considered: a bespoke pin-add picker — rejected because the retired split-action pattern this
// band replaces is exactly what a second bespoke picker would reintroduce. Routes the one
// remaining add action through `SphereSelectDialogOrganism`, the same shared selector
// `RelationshipValueEditorMolecule` already uses for its "choose spheres" affordance. See spec
// Reuse Audit.
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import SphereSelectDialogOrganism from '../../../organisms/marketplace/SphereSelectDialogOrganism';
import type { SphereSearchResult } from '../../../../hooks/useSphereSearch';
import SphereRefChipMolecule from '../../SphereRefChip/SphereRefChipMolecule';
import { RollupSetModel, addPin, removePin } from '../rollupSetModel';
import './AlwaysIncludeBand.scss';

export interface AlwaysIncludeBandProps {
  value: RollupSetModel;
  onChange: (next: RollupSetModel) => void;
  disabled?: boolean;
  className?: string;
}

const AlwaysIncludeBand: React.FC<AlwaysIncludeBandProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleRemovePin = useCallback(
    (sphereId: string) => onChange(removePin(value, sphereId)),
    [value, onChange]
  );

  const handleSelect = useCallback(
    (sphere: SphereSearchResult): boolean => {
      onChange(addPin(value, sphere.id));
      return true;
    },
    [value, onChange]
  );

  const hasPins = value.pins.length > 0;
  const helperCopy = hasPins
    ? t(
        'statusFactors.set.alwaysInclude.helperFilled',
        'these stay in even if they stop matching the conditions'
      )
    : t(
        'statusFactors.set.alwaysInclude.helperEmpty',
        'nothing added by hand — the conditions alone decide'
      );

  return (
    <div className={`always-include-band ${className}`} data-testid="always-include-band">
      <div className="always-include-band__header">
        <span className="always-include-band__label">
          {t('statusFactors.set.alwaysInclude.label', 'Always include')}
        </span>
        <span className="always-include-band__caption">{helperCopy}</span>
      </div>

      {hasPins && (
        <p className="always-include-band__quip">
          {t(
            'statusFactors.set.alwaysInclude.quip',
            'Hand-added spheres are a union hiding inside a set — honest at two or three, and a sign the conditions are wrong at twenty.'
          )}
        </p>
      )}

      <div className="always-include-band__pins">
        {value.pins.map((id) => (
          <SphereRefChipMolecule
            key={id}
            sphereId={id}
            readOnly={disabled}
            onRemove={() => handleRemovePin(id)}
            className="always-include-band__pin-chip"
          />
        ))}
        {!disabled && (
          <ButtonAtom
            variant="dashed"
            size="small"
            icon="pi pi-plus"
            label={t('statusFactors.set.alwaysInclude.addButton', 'Always include a sphere')}
            onClick={() => setPickerOpen(true)}
            className="always-include-band__add-btn"
            data-testid="always-include-add"
          />
        )}
      </div>

      {pickerOpen && (
        <SphereSelectDialogOrganism
          open
          onClose={() => setPickerOpen(false)}
          titleKey="statusFactors.set.alwaysInclude.pickerTitle"
          onSelect={handleSelect}
          excludedSphereIds={value.pins}
          showContextSelector={false}
        />
      )}
    </div>
  );
};

AlwaysIncludeBand.displayName = 'AlwaysIncludeBand';

export default AlwaysIncludeBand;
